import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueries } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Coffee, Droplets, Trees, Bus, Mountain, Zap, Wind, ChevronRight, MapPin, Download, Check, WifiOff, Toilet, Car, ShoppingBag, Beer, Utensils, Flame, ShowerHead, Share2, Satellite, Map } from "lucide-react";
import { OthalaIcon } from "../components/NorseIcons";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useOnlineStatus } from "../lib/useOnlineStatus";
import { getSavedRoute, putSavedRoute, deleteSavedRoute } from "../lib/offlineDB";
import { coverImageFor } from "../lib/stockImages";
import { track } from "../lib/telemetry";
import { shareLink } from "../lib/share";
import TransitSection from "../components/TransitSection";
import PhotoGallery from "../components/PhotoGallery";

const LEAD_LABEL = {
  strictly_on_lead: "Strictly on lead",
  off_lead_allowed: "Off-lead allowed",
  fully_enclosed: "Fully enclosed",
};
const ENERGY_LABEL = { chill: "Chill", high_energy: "High energy" };

// Terracotta picnic-bench marker — different colour/emoji from the sage
// trails (⛰) and burnt-orange campsites (⛺) so basecamps read as a third,
// distinct pin type on the shared map.
function basecampIcon() {
  return L.divIcon({
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `<div class="td-marker" style="background:#D19C4C;color:#2C3A2E;">🪑</div>`,
  });
}

function Pill({ children, tone = "sand", testid }) {
  const map = {
    sand: "bg-brand-sand text-brand-subtle border-brand-border",
    primary: "bg-brand-primary/10 text-brand-primary border-brand-primary/30",
    accent: "bg-brand-accent/15 text-brand-accent border-brand-accent/30",
    warn: "bg-brand-error/10 text-brand-error border-brand-error/30",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[tone]}`}>
      {children}
    </span>
  );
}

// Prefix so basecamp offline entries don't collide with trail IDs in the
// existing `saved_routes` object store.
const IDB_KEY = (id) => `bc:${id}`;

export default function BasecampDetail() {
  const { id } = useParams();
  const online = useOnlineStatus();
  const [saved, setSaved] = useState(false);
  const [isSatellite, setIsSatellite] = useState(false);

  const bc = useQuery({
    queryKey: ["basecamp", id],
    queryFn: async () => {
      if (!online) {
        const cached = await getSavedRoute(IDB_KEY(id));
        if (cached) return cached;
        throw new Error("Offline and this basecamp isn't saved for offline use.");
      }
      try {
        return (await api.get(`/basecamps/${id}`)).data;
      } catch (e) {
        const cached = await getSavedRoute(IDB_KEY(id));
        if (cached) return cached;
        throw e;
      }
    },
  });

  // Trails-near-here — fetched in parallel for each linked id.
  const linkedIds = bc.data?.linked_trail_ids || [];
  const linkedTrails = useQueries({
    queries: linkedIds.map((tid) => ({
      queryKey: ["loc", tid],
      queryFn: async () => (await api.get(`/locations/${tid}`)).data,
      enabled: !!tid && online,
      retry: 0,
    })),
  });

  // Track offline state on mount + when saves happen elsewhere.
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getSavedRoute(IDB_KEY(id)).then((r) => { if (!cancelled) setSaved(!!r); }).catch(() => {});
    load();
    window.addEventListener("td-offline-changed", load);
    return () => { cancelled = true; window.removeEventListener("td-offline-changed", load); };
  }, [id]);

  // Fire a single `viewed_basecamp` telemetry event once per successful load.
  // Uses the existing fire-and-forget `track()` helper which already swallows
  // network errors — keeping this useEffect body deliberately tiny.
  const loadedId = bc.data?.basecamp_id;
  useEffect(() => {
    if (!loadedId) return;
    // Session flag set by BasecampsCarousel when the "Perfect lunch stop"
    // chip is active. Attributes this open to lunch intent so admins can
    // see which basecamps genuinely convert food-hungry walkers.
    let viaLunchFilter = false;
    try {
      viaLunchFilter = sessionStorage.getItem("td_lunch_intent") === "1";
    } catch { /* ignore */ }
    track("viewed_basecamp", {
      basecamp_id: loadedId,
      bus_distance_meters: bc.data?.bus_distance_meters ?? null,
      energy_match: bc.data?.energy_match ?? null,
      via_lunch_filter: viaLunchFilter,
    });
    // Only fire once per unique basecamp id — refetches on the same detail
    // page shouldn't inflate the counter.
  }, [loadedId]);

  const toggleOffline = async () => {
    if (!bc.data) return;
    try {
      if (saved) {
        await deleteSavedRoute(IDB_KEY(id));
        setSaved(false);
        toast("Removed from offline library");
      } else {
        // Reuse the existing saved_routes store — shape matches (id/name/lat/lng)
        // and this keeps the offline library single-source in the Favourites tab.
        await putSavedRoute({
          location_id: IDB_KEY(id),
          name: `${bc.data.name} (basecamp)`,
          region: "",
          type: "basecamp",
          lat: bc.data.lat,
          lng: bc.data.lng,
          polyline: [],
          dog_amenities: [],
          short_desc: bc.data.description || "",
          ...bc.data,   // keep all basecamp-specific fields (amenities, lead_status, etc.)
        });
        setSaved(true);
        toast.success("Saved for offline — works without signal");
      }
      window.dispatchEvent(new Event("td-offline-changed"));
    } catch (e) {
      toast.error("Couldn't save offline");
    }
  };

  if (bc.isLoading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!bc.data) {
    return (
      <div className="p-8 text-brand-subtle" data-testid="basecamp-empty">
        {!online
          ? "You're offline and this basecamp isn't saved for offline use."
          : "Basecamp not found."}
        <div className="mt-4"><Link to="/discover" className="underline">Back to Discover</Link></div>
      </div>
    );
  }

  const b = bc.data;
  const distKm = typeof b.bus_distance_meters === "number" ? (b.bus_distance_meters / 1000).toFixed(1) : null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-32" data-testid={`basecamp-page-${id}`}>
      <header className="mb-4">
        <div className="td-section-label flex items-center gap-1"><OthalaIcon size={16} className="text-brand-accent" /> Basecamp</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text mt-1">{b.name}</h1>
        {b.description && (
          <p className="text-brand-text/85 text-[15px] leading-relaxed mt-2">{b.description}</p>
        )}
      </header>

      {/* Header pills — lead + energy at a glance */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="basecamp-tags">
        <Pill tone="accent" testid="bc-lead-pill">
          {LEAD_LABEL[b.lead_status] || b.lead_status}
        </Pill>
        <Pill tone={b.energy_match === "high_energy" ? "warn" : "primary"} testid="bc-energy-pill">
          {b.energy_match === "high_energy" ? <Zap size={11} /> : <Wind size={11} />}
          {ENERGY_LABEL[b.energy_match] || b.energy_match}
        </Pill>
        <button
          data-testid="bc-offline-toggle"
          onClick={toggleOffline}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
            saved
              ? "bg-brand-primary text-white border-brand-primary"
              : "bg-brand-sand text-brand-subtle border-brand-border hover:bg-brand-primary/10 hover:text-brand-primary hover:border-brand-primary/40"
          }`}
        >
          {saved ? <><Check size={11} /> Ready offline</> : <><Download size={11} /> Save offline</>}
        </button>
        <button
          data-testid="share-basecamp-btn"
          onClick={() =>
            shareLink({
              url: `${window.location.origin}/api/og/basecamp/${b.basecamp_id || id}`,
              title: "Check out this basecamp on TrailDog!",
              text: `I just found this great dog-friendly basecamp on TrailDog: ${b.name}. Check it out here!`,
              event: "share_basecamp",
              props: { basecamp_id: b.basecamp_id || id },
            })
          }
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-brand-sand text-brand-subtle border-brand-border hover:bg-brand-primary/10 hover:text-brand-primary hover:border-brand-primary/40 transition-colors"
        >
          <Share2 size={11} /> Share
        </button>
      </div>

      {/* Mini map — trailhead pin exactly at lat/lng */}
      <div className="td-card overflow-hidden h-56 mb-5 relative" data-testid="basecamp-mini-map">
        <button
          type="button"
          data-testid="map-chip-satellite"
          onClick={() => setIsSatellite((x) => !x)}
          className={`absolute top-3 left-14 z-[600] td-chip ${isSatellite ? "td-chip-active" : ""} !py-1 !px-2.5`}
          title="Toggle high-res satellite imagery"
        >
          {isSatellite ? <Map size={13} /> : <Satellite size={13} />} {isSatellite ? "Map View" : "Satellite"}
        </button>
        <MapContainer
          center={[b.lat, b.lng]}
          zoom={14}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          {!isSatellite && (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
              maxNativeZoom={16}
            />
          )}
          {isSatellite && (
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
              maxNativeZoom={19}
            />
          )}
          <Marker position={[b.lat, b.lng]} icon={basecampIcon()} />
        </MapContainer>
      </div>

      {/* Amenities */}
      <section className="td-card p-5 mb-5" data-testid="basecamp-amenities">
        <div className="td-section-label mb-2">Amenities</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { on: b.has_water_tap, Icon: Droplets, label: "Water tap", key: "water_tap" },
            { on: b.dog_waste_bins, Icon: Trees, label: "Waste bins", key: "waste_bins" },
            { on: b.cafe_on_site, Icon: Coffee, label: "Cafe", key: "cafe" },
            { on: b.has_toilets, Icon: Toilet, label: "Toilets", key: "toilets" },
            { on: b.has_car_park, Icon: Car, label: "Car park", key: "car_park" },
            { on: b.has_shop, Icon: ShoppingBag, label: "Shop", key: "shop" },
            { on: b.pub_nearby, Icon: Beer, label: "Pub nearby", key: "pub" },
            { on: b.picnic_tables, Icon: Utensils, label: "Picnic tables", key: "picnic" },
            { on: b.bbq_allowed, Icon: Flame, label: "BBQ area", key: "bbq" },
            { on: b.dog_wash_station, Icon: ShowerHead, label: "Dog wash", key: "dog_wash" },
          ].map(({ on, Icon, label, key }) => (
            <div
              key={key}
              data-testid={`bc-amenity-${key}`}
              className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm ${
                on ? "bg-brand-primary/10 border-brand-primary/40 text-brand-text" : "bg-brand-sand/40 border-brand-border/60 text-brand-subtle line-through"
              }`}
            >
              <Icon size={14} className={on ? "text-brand-primary" : "text-brand-subtle"} />
              {label}
            </div>
          ))}
        </div>
      </section>

      {/* Transit last-mile */}
      <section className="td-card p-5 mb-5" data-testid="basecamp-transit">
        <div className="td-section-label mb-1.5 flex items-center gap-1">
          <Bus size={11} /> Transit — last mile
        </div>
        {distKm || b.bus_path_quality ? (
          <div className="text-sm text-brand-text/90 flex flex-wrap items-center gap-3">
            {distKm && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={13} className="text-brand-primary" />
                <strong>{distKm} km</strong> from nearest bus stop
              </span>
            )}
            {b.bus_path_quality && <span className="text-brand-subtle">· {b.bus_path_quality}</span>}
          </div>
        ) : (
          <p className="text-sm text-brand-subtle">No transit info recorded yet.</p>
        )}
        {!online && (
          <div className="mt-3 flex items-start gap-2 p-2 rounded-lg bg-brand-accent/10 border border-brand-accent/30 text-[11px] text-brand-accent">
            <WifiOff size={12} className="mt-0.5" />
            <span>Live schedules unavailable offline — tap through to Google Maps for real-time buses when back online.</span>
          </div>
        )}
      </section>

      {/* Live Google Directions transit routing — same component the trail
          and campsite pages use. Wired to the basecamp-specific endpoint via
          resourcePath, and to the prefixed IDB entry via offlineKey so the
          offline deep-link fallback still resolves. */}
      <TransitSection
        locationId={id}
        resourcePath="basecamps"
        offlineKey={IDB_KEY(id)}
      />

      {/* Walker photos — parity with trails/campsites. Uses `basecampId` so
          the gallery hits /api/basecamps/:id/photos instead of the location
          endpoint. */}
      <PhotoGallery basecampId={id} name={b.name} />

      {/* Trails near here */}
      {linkedIds.length > 0 && (
        <section className="mb-4" data-testid="basecamp-linked-trails">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading text-lg text-brand-text flex items-center gap-1.5">
              <Mountain size={16} className="text-brand-primary" /> Trails near here
            </h2>
            <span className="text-xs text-brand-subtle">{linkedIds.length}</span>
          </div>
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4">
            {linkedTrails.map((q, i) => {
              const t = q.data;
              const tid = linkedIds[i];
              if (!t) {
                return (
                  <div key={tid} className="shrink-0 w-56 td-card p-3 opacity-60">
                    <div className="text-xs text-brand-subtle">Trail unavailable</div>
                    <div className="font-heading text-sm truncate">{tid}</div>
                  </div>
                );
              }
              return (
                <Link
                  key={tid}
                  to={`/location/${tid}`}
                  data-testid={`bc-linked-trail-${tid}`}
                  className="shrink-0 w-56 td-card overflow-hidden snap-start hover:shadow-md transition-shadow"
                >
                  <div className="h-24 bg-brand-sand overflow-hidden">
                    <img
                      src={coverImageFor(t)}
                      alt={t.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2.5">
                    <div className="text-[11px] text-brand-subtle truncate">{t.region || "United Kingdom"}</div>
                    <div className="font-heading text-sm text-brand-text truncate">{t.name}</div>
                    <div className="text-[11px] text-brand-primary flex items-center gap-0.5 mt-0.5">
                      Open <ChevronRight size={11} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <p className="text-xs text-brand-subtle mt-4">
        <Link to="/discover" className="underline">← Back to Discover</Link>
      </p>
    </div>
  );
}
